/**
 * Who is signed in — the one question the whole application branches on.
 *
 * Not in the plan's file list, and it exists for the same reason `Field`,
 * `ListState` and `overlayRoot` did in tasks 21-23: the plan's Task 25 produces
 * a `LoginPage` and a `HomePage` and never says how the application decides
 * which one to render, nor what happens on a reload with a live cookie. Left in
 * `App.tsx` it would be a piece of security-relevant state with no test of its
 * own.
 *
 * ## Three decisions
 *
 * 1. **`checking` is a real state, not `null`.** The session is an `HttpOnly`
 *    cookie; JavaScript cannot read it, so the only way to learn whether one
 *    exists is to ask the API. Collapsing "we have not asked yet" into "signed
 *    out" renders the login screen to somebody who is already signed in, every
 *    reload — and if they type into it, spends one of their five attempts.
 *
 * 2. **Any failure of the probe means anonymous.** A 401 is the ordinary case.
 *    A network failure is not, but the safe reading of "I cannot tell whether
 *    you are signed in" is "you are not": showing the shell to an unauthenticated
 *    user, with nav that will 401 on every click, is worse than one avoidable
 *    sign-in.
 *
 * 3. **`signOut` clears local state whatever the API says.** If the POST fails,
 *    the cookie may or may not still be live — but continuing to show an
 *    authenticated screen after the agent asked to leave is the one outcome
 *    that is definitely wrong. On a shared call-centre workstation that is the
 *    whole point of the button.
 *
 * The user's `locale`, `theme` and `density` come back on the resource and ARE
 * applied, here, to the appearance store.
 *
 * THE SERVER IS AUTHORITATIVE (decision S-5). localStorage is a cache so a
 * signed-out boot has something to render; the moment we know who this is, the
 * account's stored preferences win. That is the only reason to keep them
 * server-side — they follow the person to another machine.
 *
 * One visible flip is accepted when the cache and the server disagree.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, api, type ApiUser } from '../../lib/api/client';
import { normaliseAppearance, useAppearance } from '../../design-system/providers/appearance';
import type { Density, Locale, Theme } from '../../design-system/providers/appearance';

/**
 * Applies the account's stored preferences THROUGH `AppearanceProvider`'s own
 * setters, not around them.
 *
 * An earlier version wrote `localStorage` and the DOM directly
 * (`writeStoredAppearance` + `applyAppearance`), which left
 * `AppearanceProvider`'s React state holding the pre-login values. That state
 * is what `I18nProvider` reads and what the provider's own effect re-stamps
 * onto the document on every subsequent render — so the very next switch
 * touched by the user reverted whatever this function had just written. Going
 * through `setTheme` / `setDensity` / `setLocale` updates the ONE state the
 * provider owns; its existing effects (`applyAppearance` on the DOM,
 * `writeStoredAppearance` on the cache) then fire from that single source of
 * truth, the same as any other switch flip.
 *
 * Callable only from inside `AppearanceProvider` — `useSession` is mounted by
 * `App`, which `main.tsx` nests inside `AppearanceProvider` (and
 * `I18nProvider`) for exactly this reason.
 */
function applyServerAppearance(
  user: ApiUser,
  setTheme: (theme: Theme) => void,
  setDensity: (density: Density) => void,
  setLocale: (locale: Locale) => void,
): void {
  const state = normaliseAppearance({
    theme: user.theme,
    density: user.density,
    locale: user.locale,
  });
  setTheme(state.theme);
  setDensity(state.density);
  setLocale(state.locale);
}

export type Session =
  | { status: 'checking' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: ApiUser };

export interface SessionControls {
  session: Session;
  /** Called by the login screen once the API has issued a cookie. */
  signIn: (user: ApiUser) => void;
  signOut: () => Promise<void>;
}

export function useSession(): SessionControls {
  const { setTheme, setDensity, setLocale } = useAppearance();
  const [session, setSession] = useState<Session>({ status: 'checking' });

  useEffect(() => {
    // The cleanup ABORTS the request; it does not guard the setState that
    // follows, and that is deliberate.
    //
    // The familiar `let alive = true` / `if (alive) setState(...)` guard is a
    // React 17 habit. React 18 removed the "state update on an unmounted
    // component" warning precisely because the guard it provoked is an
    // anti-pattern: a setState on an unmounted component is a documented no-op.
    // A mutation removing such a guard from this file changed nothing in any
    // test, in either direction — an unkillable line is a line the next person
    // deletes without knowing what it was for, so it is not here to be deleted.
    //
    // Aborting IS worth doing: it stops the request itself, which the guard
    // never did.
    const controller = new AbortController();

    api
      .me(controller.signal)
      .then((user) => {
        applyServerAppearance(user, setTheme, setDensity, setLocale);
        setSession({ status: 'authenticated', user });
      })
      .catch(() => {
        // Deliberately not branching on the code. `unauthenticated` is the
        // ordinary answer and everything else is a reason we cannot tell,
        // which resolves the same way. See note 2.
        setSession({ status: 'anonymous' });
      });

    return () => {
      controller.abort();
    };
    // `setTheme` / `setDensity` / `setLocale` are each `useCallback`d with an
    // empty dependency array in `AppearanceProvider` — stable for the life of
    // the provider — so including them here does not re-run this effect on
    // every render; it only documents the real dependency.
  }, [setTheme, setDensity, setLocale]);

  const signIn = useCallback(
    (user: ApiUser) => {
      applyServerAppearance(user, setTheme, setDensity, setLocale);
      setSession({ status: 'authenticated', user });
    },
    [setTheme, setDensity, setLocale],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch (error) {
      // Swallowed on purpose, and narrowly: an ApiError means the API answered
      // and refused, which changes nothing about the decision below. Anything
      // else is a programming fault and must not be hidden.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      setSession({ status: 'anonymous' });
    }
  }, []);

  return { session, signIn, signOut };
}
